import { useEffect, useMemo, useState } from 'react';

// Types for better type safety
interface CartItem {
  merchandise: {
    id: string;
    product: {
      id: string;
    };
  };
  quantity: number;
}

interface Cart {
  id?: string; // Make this optional to match Shopify type
  lines: CartItem[];
}

interface InventoryData {
  quantityAvailable: number | undefined;
  availableForSale: boolean;
  isLoading: boolean;
  error: string | null;
}

interface InventoryMap {
  [key: string]: InventoryData;
}

interface CartInventoryHook {
  inventoryMap: InventoryMap;
  isLoading: boolean;
  error: string | null;
  getItemInventory: (item: CartItem) => InventoryData | undefined;
  refreshInventory: () => void;
}

/**
 * Custom hook to manage inventory data for cart items
 *
 * @param cart The current cart object
 * @returns {CartInventoryHook} Inventory data and helper functions
 */
export function useCartInventory(cart: Cart | null): CartInventoryHook {
  const [inventoryMap, setInventoryMap] = useState<InventoryMap>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Get unique variant IDs from the cart
  const variantIds = useMemo(() => {
    if (!cart || !cart.lines || cart.lines.length === 0) return [];

    return [...new Set(cart.lines.map((item) => item.merchandise.id))];
  }, [cart]);

  // Fetch inventory data for all variants in the cart
  useEffect(() => {
    async function fetchInventoryData() {
      if (variantIds.length === 0 || !cart) return;

      setIsLoading(true);
      setError(null);

      const newInventoryMap = { ...inventoryMap };
      let hasErrors = false;

      // Process variants in batches to avoid too many simultaneous requests
      const batchSize = 5;
      const batches = [];

      for (let i = 0; i < variantIds.length; i += batchSize) {
        batches.push(variantIds.slice(i, i + batchSize));
      }

      for (const batch of batches) {
        await Promise.all(
          batch.map(async (variantId) => {
            try {
              // Find the cart item for this variant to get the product ID
              const cartItem = cart.lines.find((item) => item.merchandise.id === variantId);
              if (!cartItem) {
                console.error(`Cart item not found for variant ${variantId}`);
                return;
              }

              // Get the product ID directly from the cart item structure
              const productId = cartItem.merchandise.product.id;

              // Skip if we already have fresh data for this variant
              if (newInventoryMap[variantId] && !newInventoryMap[variantId].isLoading) {
                return;
              }

              // Mark as loading in the map
              newInventoryMap[variantId] = {
                quantityAvailable: newInventoryMap[variantId]?.quantityAvailable || undefined,
                availableForSale: newInventoryMap[variantId]?.availableForSale || false,
                isLoading: true,
                error: null
              };

              console.log(`Fetching inventory: Product ID=${productId}, Variant ID=${variantId}`);

              // Updated API endpoint with proper error handling
              const response = await fetch(
                `/api/products/inventory?id=${encodeURIComponent(productId)}&variantId=${encodeURIComponent(variantId)}`
              );

              if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                throw new Error(errorData.error || `Error fetching inventory: ${response.status}`);
              }

              const data = await response.json();

              newInventoryMap[variantId] = {
                quantityAvailable: data.quantityAvailable,
                availableForSale: data.availableForSale,
                isLoading: false,
                error: null
              };
            } catch (err) {
              console.error(`Error fetching inventory for variant ${variantId}:`, err);
              hasErrors = true;

              // Keep partial data but mark the error
              newInventoryMap[variantId] = {
                quantityAvailable: newInventoryMap[variantId]?.quantityAvailable || undefined,
                availableForSale: newInventoryMap[variantId]?.availableForSale || false,
                isLoading: false,
                error: err instanceof Error ? err.message : 'Error fetching inventory'
              };
            }
          })
        );

        // Update state after each batch to show progressive loading
        setInventoryMap({ ...newInventoryMap });
      }

      setIsLoading(false);
      if (hasErrors) {
        setError('Some inventory data could not be loaded');
      }
    }

    fetchInventoryData();
  }, [variantIds, refreshCounter, cart]);

  /**
   * Get inventory data for a specific cart item
   */
  const getItemInventory = (item: CartItem): InventoryData | undefined => {
    if (!item || !item.merchandise || !item.merchandise.id) return undefined;

    return inventoryMap[item.merchandise.id];
  };

  /**
   * Force refresh of inventory data
   */
  const refreshInventory = () => {
    setRefreshCounter((prev) => prev + 1);
  };

  return {
    inventoryMap,
    isLoading,
    error,
    getItemInventory,
    refreshInventory
  };
}
